import {
  useEffect,
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { ArrowRight, Box, Check, Cpu, MemoryStick, Sparkles } from "lucide-react";
import heroPackshot from "@/assets/products/tv6-ultra/home-hardware-packshot.jpg";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";

const NETWORK_ROUTES = [
  "M-80 155 C250 70 415 315 720 1040",
  "M1520 125 C1170 90 1060 355 720 1040",
  "M25 470 C300 360 500 500 720 1040",
  "M1415 500 C1130 390 920 515 720 1040",
  "M230 -40 C380 185 550 245 720 1040",
  "M1210 -35 C1045 180 880 270 720 1040",
] as const;

const HERO_PACKETS = [
  { d: NETWORK_ROUTES[0], duration: 11, delays: [0, -3.7, -7.3] },
  { d: NETWORK_ROUTES[1], duration: 13, delays: [-2, -6.3, -10.7] },
] as const;

type HeroPointerStyle = CSSProperties & {
  "--hero-pointer-x": string;
  "--hero-pointer-y": string;
  "--hero-pointer-opacity": number;
};

const HERO_POINTER_STYLE: HeroPointerStyle = {
  "--hero-pointer-x": "50%",
  "--hero-pointer-y": "24%",
  "--hero-pointer-opacity": 0,
};

const POINTER_UPDATE_INTERVAL_MS = 32;

function NetworkField({ highlighted = false }: { highlighted?: boolean }) {
  return (
    <svg
      aria-hidden
      focusable="false"
      viewBox="0 0 1440 820"
      preserveAspectRatio="xMidYMin slice"
      className="hero-network-field h-full w-full"
    >
      <g className={highlighted ? "hero-network-lines-highlighted" : "hero-network-lines"}>
        {NETWORK_ROUTES.map((route) => (
          <path key={route} d={route} />
        ))}
      </g>

      {!highlighted && (
        <g className="hero-network-packets">
          {HERO_PACKETS.flatMap(({ d, duration, delays }) =>
            delays.map((delay) => (
              <g
                key={`${d}:${delay}`}
                className="hero-network-packet"
                style={{
                  offsetPath: `path("${d}")`,
                  animationDuration: `${duration}s`,
                  animationDelay: `${delay}s`,
                }}
              >
                <circle className="hero-network-packet-halo" r={8} />
                <circle className="hero-network-packet-core" r={3} />
              </g>
            )),
          )}
        </g>
      )}

      <g className={highlighted ? "hero-network-nodes-highlighted" : "hero-network-nodes"}>
        {[
          [140, 130],
          [315, 190],
          [470, 335],
          [1300, 130],
          [1120, 220],
          [970, 390],
          [175, 425],
          [380, 455],
          [1260, 450],
          [1050, 470],
        ].map(([cx, cy], index) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={index % 3 === 0 ? 4 : 2.5} />
        ))}
      </g>
    </svg>
  );
}

export function Hero() {
  const { messages, site, href } = useSite();
  const sectionRef = useRef<HTMLElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastPointerUpdateRef = useRef(0);
  const pointerRef = useRef({ x: 0, y: 0 });
  const productsHref = localizeUrl("/products", site.locale, href);
  const flagshipHref = localizeUrl("/products/inext-tv6-ultra", site.locale, href);
  const salesHref = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const proofPoints = ["4K UHD", "Android", messages.customServiceWhiteLabelingTitle];

  useEffect(
    () => () => {
      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current);
    },
    [],
  );

  const resetPointer = () => {
    const highlight = highlightRef.current;
    if (!highlight) return;

    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    lastPointerUpdateRef.current = 0;
    highlight.style.setProperty("--hero-pointer-opacity", "0");
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (
      event.pointerType !== "mouse" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    pointerRef.current = { x: event.clientX, y: event.clientY };
    if (event.timeStamp - lastPointerUpdateRef.current < POINTER_UPDATE_INTERVAL_MS) return;
    lastPointerUpdateRef.current = event.timeStamp;
    if (animationFrameRef.current !== null) return;

    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null;
      const section = sectionRef.current;
      const highlight = highlightRef.current;
      if (!section || !highlight) return;

      const rect = section.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (pointerRef.current.x - rect.left) / rect.width));
      const y = Math.min(1, Math.max(0, (pointerRef.current.y - rect.top) / rect.height));

      highlight.style.setProperty("--hero-pointer-x", `${x * 100}%`);
      highlight.style.setProperty("--hero-pointer-y", `${y * 100}%`);
      highlight.style.setProperty("--hero-pointer-opacity", "1");
    });
  };

  return (
    <section
      ref={sectionRef}
      data-home-hero
      className="relative isolate overflow-hidden border-b border-border/60"
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="hero-aurora-parallax animate-aurora absolute -inset-6">
          <div className="h-full w-full" style={{ background: "var(--gradient-aurora)" }} />
        </div>
        <div className="hero-grid-parallax absolute -inset-4 bg-grid opacity-60" />
        <div className="hero-network-parallax absolute -inset-x-8 top-0 h-full min-h-[44rem]">
          <NetworkField />
        </div>
        <div
          ref={highlightRef}
          className="hero-network-highlight absolute -inset-x-8 top-0 h-full min-h-[44rem]"
          style={HERO_POINTER_STYLE}
        >
          <NetworkField highlighted />
        </div>
      </div>

      <div className="relative mx-auto max-w-7xl px-6 pb-12 pt-14 sm:pb-16 sm:pt-20 lg:pb-20">
        <div className="grid items-center gap-9 sm:gap-12 lg:grid-cols-[0.92fr_1.08fr] lg:gap-14">
          <div>
            <div className="inline-flex animate-fade-up items-center gap-2 rounded-full border border-border bg-background/85 px-3 py-1 text-xs font-medium text-muted-foreground shadow-[var(--shadow-soft)]">
              <Sparkles className="h-3 w-3 text-primary" />
              {messages.heroEyebrow}
            </div>

            <h1 className="mt-5 max-w-3xl text-balance text-[2.65rem] font-semibold leading-[0.98] tracking-[-0.04em] text-foreground animate-fade-up sm:mt-6 sm:text-6xl lg:text-7xl">
              {messages.heroTitle} <span className="text-gradient">{messages.heroTitleAccent}</span>
            </h1>

            <p
              className="mt-5 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground animate-fade-up sm:mt-6 sm:text-lg"
              style={{ animationDelay: "120ms" }}
            >
              {messages.heroDescription}
            </p>

            <div
              className="mt-7 flex flex-col items-start gap-3 animate-fade-up sm:mt-8 sm:flex-row"
              style={{ animationDelay: "180ms" }}
            >
              <Button
                asChild
                size="lg"
                className="group h-12 rounded-xl px-6 text-sm font-medium animate-glow"
              >
                <a href={productsHref}>
                  {messages.heroModelsCta}
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-12 rounded-xl bg-background/70 px-6 text-sm font-medium backdrop-blur"
              >
                <a href={salesHref}>{messages.heroQuoteCta}</a>
              </Button>
            </div>

            <ul
              className="mt-6 flex flex-wrap gap-x-5 gap-y-2 animate-fade-up sm:mt-7"
              style={{ animationDelay: "230ms" }}
            >
              {proofPoints.map((point) => (
                <li
                  key={point}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/80"
                >
                  <Check className="h-3.5 w-3.5 text-primary" />
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <a
            href={flagshipHref}
            className="group relative block animate-fade-up"
            style={{ animationDelay: "120ms" }}
          >
            <div
              aria-hidden
              className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-primary/15 blur-3xl"
            />
            <figure className="relative overflow-hidden rounded-[2rem] border border-primary/20 bg-card shadow-[0_30px_80px_-38px_rgba(14,88,170,0.5)]">
              <img
                src={heroPackshot}
                alt={messages.heroDeviceImageAlt}
                width={1200}
                height={800}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="aspect-[3/2] h-auto w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#071b3b]/80 via-[#071b3b]/20 to-transparent" />

              <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/55 bg-white/85 px-3 py-1.5 text-xs font-semibold text-slate-900 shadow-sm backdrop-blur sm:left-6 sm:top-6">
                <Box className="h-3.5 w-3.5 text-primary" />
                {messages.lineupFlagshipBadge} · inext TV6 Ultra
              </div>

              <figcaption className="absolute inset-x-4 bottom-4 flex items-end justify-between gap-3 sm:inset-x-6 sm:bottom-6">
                <div className="flex flex-wrap gap-2 text-xs font-medium text-white">
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-slate-950/55 px-2.5 py-1.5 backdrop-blur">
                    <Cpu className="h-3.5 w-3.5 text-sky-300" /> H618
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-slate-950/55 px-2.5 py-1.5 backdrop-blur">
                    <MemoryStick className="h-3.5 w-3.5 text-sky-300" /> 4 / 32 GB
                  </span>
                  <span className="rounded-lg border border-white/20 bg-slate-950/55 px-2.5 py-1.5 backdrop-blur">
                    Android 12
                  </span>
                </div>
                <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-950 shadow-sm sm:inline-flex">
                  {messages.productMenuFeaturedCta}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </figcaption>
            </figure>
          </a>
        </div>
      </div>
    </section>
  );
}
